import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) out[i] = rawData.charCodeAt(i);
  return out;
}

function isIosNonStandalone() {
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return isIOS && !standalone;
}

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    queueMicrotask(() => {
      setSupported(ok);
      if (ok) setPermission(Notification.permission);
      else setHint("Your browser doesn’t support push notifications.");
    });
  }, []);

  const getReg = useCallback(async () => {
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    return reg;
  }, []);

  const syncState = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await getReg();
      const existing = await reg.pushManager.getSubscription();
      queueMicrotask(() => setSubscribed(!!existing));
    } catch (_e) { /* ignore */ }
  }, [supported, getReg]);

  useEffect(() => { if (supported) syncState(); }, [supported, syncState]);

  const subscribe = useCallback(async () => {
    if (!supported) return;
    if (isIosNonStandalone()) {
      queueMicrotask(() => setHint("On iPhone/iPad, install SJDS.app to your home screen first (Share → Add to Home Screen)."));
      return;
    }
    queueMicrotask(() => setBusy(true));
    try {
      const perm = await Notification.requestPermission();
      queueMicrotask(() => setPermission(perm));
      if (perm !== "granted") {
        queueMicrotask(() => setHint("Notifications were blocked. Enable them in your browser settings."));
        return;
      }
      const reg = await getReg();
      const { data } = await http.get("/push/vapid-public-key");
      const key = urlBase64ToUint8Array(data.public_key);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      const j = sub.toJSON();
      await http.post("/push/subscribe", { endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth });
      queueMicrotask(() => { setSubscribed(true); setHint("You’ll receive emergency & surf alerts."); });
    } catch (e) {
      queueMicrotask(() => setHint("Could not enable notifications. Try again."));
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  }, [supported, getReg]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    queueMicrotask(() => setBusy(true));
    try {
      const reg = await getReg();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const j = sub.toJSON();
        await sub.unsubscribe();
        try {
          await http.delete("/push/unsubscribe", { data: { endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth } });
        } catch (_e) { /* not authed or already gone — ok */ }
      }
      queueMicrotask(() => { setSubscribed(false); setHint(""); });
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  }, [supported, getReg]);

  return { supported, permission, subscribed, busy, hint, subscribe, unsubscribe, reset: useCallback(async () => {
    if (!supported) return;
    queueMicrotask(() => setBusy(true));
    try {
      // First fully unsubscribe (server + browser), then re-subscribe so the
      // VAPID key + push endpoint are fresh.
      const reg = await getReg();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const j = sub.toJSON();
        try { await sub.unsubscribe(); } catch (_e) { /* ignore */ }
        try {
          await http.delete("/push/unsubscribe", { data: { endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth } });
        } catch (_e) { /* ignore */ }
      }
      // Re-subscribe so the user immediately starts receiving alerts again.
      const perm = Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();
      queueMicrotask(() => setPermission(perm));
      if (perm !== "granted") {
        queueMicrotask(() => { setSubscribed(false); setHint("Notifications blocked. Enable them in your browser settings."); });
        return;
      }
      const { data } = await http.get("/push/vapid-public-key");
      const key = urlBase64ToUint8Array(data.public_key);
      const fresh = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      const f = fresh.toJSON();
      await http.post("/push/subscribe", { endpoint: f.endpoint, p256dh: f.keys.p256dh, auth: f.keys.auth });
      queueMicrotask(() => { setSubscribed(true); setHint("Notifications reset — you’ll receive fresh alerts."); });
    } catch (_e) {
      queueMicrotask(() => setHint("Reset failed. Try again."));
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  }, [supported, getReg]) };
}
